import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
function pickFields<T extends object>(source: T, update: Partial<T>): Partial<T> {
  const picked: Partial<T> = {};
  for (const field of Object.keys(update) as (keyof T)[]) {
    picked[field] = source[field];
  }
  return picked;
}

/**
 * Query and mutation hooks for a member's per-org preferences that the
 * backend serves at `/user/github/orgs/{org}/{resource}`, every field
 * resolved to its default.
 *
 * Each control saves independently, so two mutations can overlap. Every
 * mutation callback only touches the fields its own update carried: a
 * delayed failure of one save must not roll back another save that already
 * landed.
 */
export function createUserOrgSettingsHooks<T extends object>({
  resource,
  errorMessage
}: {
  resource: string;
  errorMessage: string;
}) {
  function queryKey(org: string) {
    return [resource, org] as const;
  }
  function useSettings(org, t0) {
    const $ = _c(10);
    let t1;
    if ($[0] !== t0) {
      t1 = t0 === undefined ? {} : t0;
      $[0] = t0;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const {
      suspense: t2
    } = t1;
    const suspense = t2 === undefined ? false : t2;
    const api = useApi();
    const t3 = org ?? "";
    let t4;
    if ($[2] !== t3) {
      t4 = queryKey(t3);
      $[2] = t3;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    let t5;
    if ($[4] !== api || $[5] !== org) {
      t5 = async () => {
        const response = await api.get(`/user/github/orgs/${org}/${resource}`);
        return response.data;
      };
      $[4] = api;
      $[5] = org;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    const t6 = !!org;
    let t7;
    if ($[7] !== suspense || $[8] !== t6) {
      t7 = {
        enabled: t6,
        suspense,
        useErrorBoundary: false
      };
      $[7] = suspense;
      $[8] = t6;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return useQuery(t4, t5, t7);
  }
  function useUpdateSettings(org) {
    const $ = _c(18);
    const api = useApi();
    const queryClient = useQueryClient();
    let t0;
    if ($[0] !== api || $[1] !== org) {
      t0 = async update => {
        const response = await api.put(`/user/github/orgs/${org}/${resource}`, update);
        return response.data;
      };
      $[0] = api;
      $[1] = org;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    const t1 = org ?? "";
    let t2;
    if ($[3] !== t1) {
      t2 = queryKey(t1);
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== org || $[6] !== queryClient) {
      t3 = async update_0 => {
        if (!org) {
          return;
        }
        const key = queryKey(org);
        await queryClient.cancelQueries(key);
        const current = queryClient.getQueryData(key);
        if (!current) {
          return {
            org,
            previous: undefined
          };
        }
        queryClient.setQueryData(key, {
          ...current,
          ...update_0
        });
        return {
          org,
          previous: pickFields(current, update_0)
        };
      };
      $[5] = org;
      $[6] = queryClient;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    let t5;
    let t6;
    if ($[8] !== queryClient) {
      t4 = (_error, _update, context) => {
        if (context?.previous) {
          const key_0 = queryKey(context.org);
          const current_0 = queryClient.getQueryData(key_0);
          if (current_0) {
            queryClient.setQueryData(key_0, {
              ...current_0,
              ...context.previous
            });
          }
        }
        toast.error(errorMessage);
      };
      t5 = (data, update_1, context_0) => {
        if (!context_0) {
          return;
        }
        const key_1 = queryKey(context_0.org);
        const current_1 = queryClient.getQueryData(key_1);
        queryClient.setQueryData(key_1, current_1 ? {
          ...current_1,
          ...pickFields(data, update_1)
        } : data);
      };
      t6 = (_data, _error_0, _update_0, context_1) => {
        if (!context_1) {
          return;
        }
        const key_2 = queryKey(context_1.org);
        if (queryClient.isMutating({
          mutationKey: key_2
        }) <= 1) {
          queryClient.invalidateQueries(key_2);
        }
      };
      $[8] = queryClient;
      $[9] = t4;
      $[10] = t5;
      $[11] = t6;
    } else {
      t4 = $[9];
      t5 = $[10];
      t6 = $[11];
    }
    let t7;
    if ($[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
      t7 = {
        mutationKey: t2,
        onMutate: t3,
        onError: t4,
        onSuccess: t5,
        onSettled: t6
      };
      $[12] = t2;
      $[13] = t3;
      $[14] = t4;
      $[15] = t5;
      $[16] = t6;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return useMutation(t0, t7);
  }
  return {
    queryKey,
    useSettings,
    useUpdateSettings
  };
}